(() => {
  'use strict';
  const panel = document.querySelector('[data-saved-books]');
  if (!panel || typeof panel.showModal !== 'function' || !window.WonderbookFavoriteStorage) return;
  const storage = (name) => { try { return window[name]; } catch (_) { return null; } };
  const store = window.WonderbookFavoriteStorage.createStore({
    shop: panel.dataset.favoritesShop,
    local: storage('localStorage'),
    session: storage('sessionStorage')
  });
  const list = panel.querySelector('[data-saved-books-list]');
  const template = panel.querySelector('[data-saved-book-template]');
  const status = panel.querySelector('[data-saved-books-status]');
  const toast = document.querySelector('[data-favorites-toast]');
  const closeButton = panel.querySelector('[data-saved-books-close]');
  const dashboard = document.querySelector('[data-wonderbook-dashboard]');
  let records = [];
  let returnFocus = null;
  let toastTimer;
  let drawerRenderVersion = 0;
  let dashboardRenderVersion = 0;
  const pendingBookDetails = new Map();
  const signInReturnKey = `wonderbook:favorites-return:${panel.dataset.favoritesShop}`;
  const root = panel.dataset.favoritesRoot.replace(/\/?$/, '/');

  function safeImage(value) {
    if (!value) return '';
    try {
      const url = new URL(value, window.location.origin);
      const trusted = url.origin === window.location.origin || url.hostname === 'cdn.shopify.com'
        || url.hostname === panel.dataset.favoritesShop;
      return trusted && url.protocol === 'https:' ? url.href : '';
    } catch (_) { return ''; }
  }

  function currentBookDetails(item) {
    if (pendingBookDetails.has(item.handle)) return pendingBookDetails.get(item.handle);
    const request = (async () => {
      const controller = new AbortController();
      const timeout = setTimeout(() => controller.abort(), 12000);
      try {
        const url = new URL(`${root}products/${encodeURIComponent(item.handle)}`, window.location.origin);
        if (url.origin !== window.location.origin) return null;
        url.searchParams.set('section_id', 'wonderpods-book-pills');
        const response = await fetch(url.href, {
          credentials: 'same-origin', cache: 'no-store', signal: controller.signal,
          headers: { Accept: 'text/html' }
        });
        if (!response.ok || (response.url && new URL(response.url).origin !== window.location.origin)) return null;
        const document = new DOMParser().parseFromString(await response.text(), 'text/html');
        const details = document.querySelector('[data-book-pills-product-id]');
        if (details?.dataset.bookPillsProductId !== item.productId) return null;
        const pills = details.querySelector('.wp-desktop-navigation__pills');
        if (!pills) return null;
        // Only the collection links generated by our product section are used.
        for (const link of pills.querySelectorAll('a')) {
          const target = new URL(link.getAttribute('href'), window.location.origin);
          if (target.origin !== window.location.origin) return null;
          link.href = target.href;
        }
        return { pills, title: details.dataset.bookPillsTitle, image: safeImage(details.dataset.bookPillsImage), restock: details.querySelector('[data-restock-card]') };
      } catch (_) {
        // A removed product or failed request leaves its saved cover usable.
        return null;
      } finally { clearTimeout(timeout); }
    })();
    pendingBookDetails.set(item.handle, request);
    request.finally(() => {
      if (pendingBookDetails.get(item.handle) === request) pendingBookDetails.delete(item.handle);
    });
    return request;
  }

  function applyRestockCard(card, restock) {
    if (!restock) return;
    const action = card.querySelector('.wp-mini-button');
    const link = restock.querySelector('a');
    if (action && link) {
      const target = new URL(link.getAttribute('href'), window.location.origin);
      if (target.origin !== window.location.origin) return;
      action.href = target.href;
      action.textContent = link.textContent;
      action.setAttribute('aria-label', link.getAttribute('aria-label'));
      action.classList.remove('wp-button--personalize');
    }
    const badge = restock.querySelector('.wp-stock-badge');
    if (badge && !card.querySelector('.wp-stock-badge')) card.querySelector('[data-saved-book-pills]')?.append(badge.cloneNode(true));
  }

  function fillBookCard(card, item, linkSelector) {
    card.querySelectorAll(linkSelector).forEach((link) => {
      link.href = `${root}products/${encodeURIComponent(item.handle)}`;
      link.setAttribute('aria-label', link.classList.contains('wp-button--personalize') ? `Tilpass ${item.title}` : item.title);
    });
    const image = card.querySelector('img');
    image.classList.add('no-book-picture__image');
    image.parentElement.classList.add('no-book-picture');
    const source = safeImage(window.NorwayBookFeatures?.imageUrl(item.productId, item.image) || item.image);
    if (source) image.src = source;
    else image.hidden = true;
    image.addEventListener('error', () => { image.hidden = true; });
    if (item.productId === '15233427505518') applyRestockCard(card, panel.querySelector('[data-world-cup-restock]')?.content.querySelector('[data-restock-card]'));
  }

  async function hydrateBookCard(card, item, linkSelector, isCurrent) {
    const details = await currentBookDetails(item);
    if (!details || !card.isConnected || !isCurrent()) return;
    card.querySelector('[data-saved-book-pills]').replaceChildren(details.pills.cloneNode(true));
    if (details.title) {
      card.querySelectorAll(linkSelector).forEach((link) => {
        link.setAttribute('aria-label', link.classList.contains('wp-button--personalize') ? `Tilpass ${details.title}` : details.title);
      });
      card.querySelector('[data-saved-book-remove]')?.setAttribute('aria-label', `Fjern ${details.title} fra lagrede bøker`);
    }
    applyRestockCard(card, details.restock);
    if (details.image) {
      const image = card.querySelector('img');
      image.hidden = false;
      const source = safeImage(window.NorwayBookFeatures?.imageUrl(item.productId, details.image) || details.image);
      if (source && image.src !== source) image.src = source;
    }
  }

  function syncButtons() {
    const saved = new Set(records.map((item) => item.productId));
    document.querySelectorAll('[data-favorite-toggle]').forEach((button) => {
      const selected = saved.has(button.dataset.favoriteId);
      button.hidden = false;
      button.setAttribute('aria-pressed', String(selected));
      button.setAttribute('aria-label', `${selected ? "Fjern" : 'Lagre'} ${button.dataset.favoriteTitle}${selected ? ' fra lagrede bøker' : ''}`);
      button.title = selected ? 'Fjern fra lagrede bøker' : "Lagre på denne enheten";
      const label = button.querySelector('[data-favorite-label]');
      if (label) label.textContent = selected ? "Bok lagret" : "Lagre bok";
    });
    document.querySelectorAll('[data-saved-books-open]').forEach((button) => {
      button.hidden = false;
      button.setAttribute('aria-expanded', String(panel.open));
      button.setAttribute('aria-label', `Lagrede bøker${records.length ? `, ${records.length} lagret` : ''}`);
    });
    document.querySelectorAll('[data-favorites-count]').forEach((counter) => {
      counter.hidden = !records.length;
      counter.textContent = String(records.length);
      counter.setAttribute('aria-hidden', 'true');
    });
  }

  function renderList() {
    const version = ++drawerRenderVersion;
    const focused = document.activeElement;
    const focusedRow = focused?.closest('[data-saved-row]');
    const oldRows = [...list.children];
    const focusedIndex = oldRows.indexOf(focusedRow);
    const focusedId = focusedRow?.dataset.savedRow;
    const focusWasRemove = focused?.matches('[data-saved-book-remove]');
    const content = document.createDocumentFragment();
    for (const item of records) {
      const row = template.content.firstElementChild.cloneNode(true);
      row.dataset.savedRow = item.productId;
      fillBookCard(row, item, '[data-saved-book-link]');
      const remove = row.querySelector('[data-saved-book-remove]');
      remove.dataset.savedBookRemove = item.productId;
      remove.setAttribute('aria-label', `Fjern ${item.title} fra lagrede bøker`);
      content.append(row);
      hydrateBookCard(row, item, '[data-saved-book-link]', () => version === drawerRenderVersion && panel.open);
    }
    list.replaceChildren(content);
    panel.querySelector('[data-saved-books-empty]').hidden = records.length > 0;
    panel.querySelector('[data-saved-books-count]').textContent = records.length
      ? `${records.length} ${records.length === 1 ? 'bok' : 'bøker'} lagret · Åpne boken for å se tilgjengelige valg og pris.` : '';
    if (panel.open && focusedIndex >= 0) {
      const rows = [...list.children];
      const next = rows.find((row) => row.dataset.savedRow === focusedId) || rows[Math.min(focusedIndex, rows.length - 1)];
      (next?.querySelector(focusWasRemove ? '[data-saved-book-remove]' : '[data-saved-book-link]') || closeButton).focus({ preventScroll: true });
    }
  }

  function renderDashboard() {
    if (!dashboard) return;
    const version = ++dashboardRenderVersion;
    const grid = dashboard.querySelector('[data-dashboard-saved-list]');
    const empty = dashboard.querySelector('[data-dashboard-saved-empty]');
    const template = dashboard.querySelector('[data-dashboard-saved-template]');
    dashboard.querySelector('[data-dashboard-saved-count]').textContent = records.length
      ? `${records.length} ${records.length === 1 ? 'bok' : 'bøker'} lagret i denne nettleseren` : 'Bøker du lagrer, vises her.';
    grid.hidden = records.length === 0;
    empty.hidden = records.length > 0;
    const content = document.createDocumentFragment();
    for (const item of records.slice(0, 4)) {
      const card = template.content.firstElementChild.cloneNode(true);
      fillBookCard(card, item, '[data-dashboard-book-link]');
      content.append(card);
      hydrateBookCard(card, item, '[data-dashboard-book-link]', () => version === dashboardRenderVersion);
    }
    grid.replaceChildren(content);
  }

  function refresh() {
    records = store.list();
    syncButtons();
    if (panel.open) renderList();
    renderDashboard();
    document.dispatchEvent(new CustomEvent('wonderbook:favorites-change'));
  }

  function announce(message) {
    clearTimeout(toastTimer);
    if (panel.open) {
      status.textContent = message;
    } else {
      toast.hidden = false;
      toast.textContent = message;
      toastTimer = setTimeout(() => { toast.hidden = true; }, 6000);
    }
  }

  function changeMessage(result) {
    if (result.persistence === 'page') return 'Nettleseren kunne ikke lagre endringen. Den går tapt når du forlater siden.';
    if (result.persistence === 'tab') return 'Nettleseren kunne ikke lagre endringen permanent. Hold fanen åpen for å beholde den.';
    return result.removed ? "Boken er fjernet fra lagrede bøker." : "Boken er lagret på denne enheten. Finn den i Lagrede bøker.";
  }

  function open(trigger) {
    if (panel.open) return;
    const menu = trigger?.closest('.wp-mobile-menu');
    returnFocus = menu ? menu.querySelector('summary') : trigger || document.activeElement;
    if (menu) menu.open = false;
    status.textContent = '';
    toast.hidden = true;
    records = store.list();
    renderList();
    panel.hidden = false;
    panel.showModal();
    document.body.classList.add('wp-saved-books-open');
    document.dispatchEvent(new CustomEvent('wonderbook:favorites-open'));
    syncButtons();
  }

  document.addEventListener('click', (event) => {
    if (!(event.target instanceof Element)) return;
    const signIn = event.target.closest('[data-favorites-sign-in]');
    if (signIn) {
      // Keep all favorite records intact. This marker only restores the panel
      // after Shopify returns to the storefront; it is not account sync.
      try { storage('sessionStorage')?.setItem(signInReturnKey, String(Date.now())); } catch (_) { /* Sign-in still works without the optional return marker. */ }
      return;
    }
    const openButton = event.target.closest('[data-saved-books-open]');
    if (openButton) { event.preventDefault(); open(openButton); return; }
    const toggle = event.target.closest('[data-favorite-toggle]');
    const remove = event.target.closest('[data-saved-book-remove]');
    if (!toggle && !remove) return;
    event.preventDefault();
    try {
      // Re-read at action time so another tab's saves/removals are respected.
      const current = store.list();
      const id = remove ? remove.dataset.savedBookRemove : toggle.dataset.favoriteId;
      const existing = current.find((item) => item.productId === id);
      const result = remove || existing ? store.remove(id) : store.set({
        productId: id,
        handle: toggle.dataset.favoriteHandle,
        title: toggle.dataset.favoriteTitle,
        image: toggle.dataset.favoriteImage
      });
      refresh();
      if (result) announce(changeMessage(result));
    } catch (_) { announce('Endringen kunne ikke lagres. Prøv igjen.'); }
  });

  closeButton.addEventListener('click', () => panel.close());
  panel.addEventListener('click', (event) => {
    if (event.target !== panel) return;
    const bounds = panel.getBoundingClientRect();
    if (event.clientX < bounds.left || event.clientX > bounds.right || event.clientY < bounds.top || event.clientY > bounds.bottom) panel.close();
  });
  panel.addEventListener('close', () => {
    drawerRenderVersion += 1;
    panel.hidden = true;
    document.body.classList.remove('wp-saved-books-open');
    syncButtons();
    if (returnFocus?.isConnected) returnFocus.focus({ preventScroll: true });
  });
  window.addEventListener('storage', (event) => {
    if (event.key === null || event.key.startsWith(store.prefix)) refresh();
  });
  function resumeAfterSignIn() {
    try {
      const session = storage('sessionStorage');
      const timestamp = Number(session?.getItem(signInReturnKey));
      if (!timestamp) return;
      session.removeItem(signInReturnKey);
      if (Date.now() - timestamp >= 0 && Date.now() - timestamp < 30 * 60 * 1000) open();
    } catch (_) { /* Favorites remain available through the regular menu. */ }
  }
  window.addEventListener('pageshow', () => { refresh(); resumeAfterSignIn(); });
  document.addEventListener('shopify:section:load', refresh);
  new MutationObserver((mutations) => {
    const addedControls = mutations.some((mutation) => [...mutation.addedNodes].some((node) => node instanceof Element
      && (node.matches('[data-favorite-toggle], [data-saved-books-open]') || node.querySelector('[data-favorite-toggle], [data-saved-books-open]'))));
    if (addedControls) syncButtons();
  }).observe(document.body, { childList: true, subtree: true });
  refresh();
  resumeAfterSignIn();
  window.WonderbookFavorites = { list: () => store.list(), open: () => open() };
})();
